import { DataTable } from "primereact/datatable";
import { TRequirement } from "./modal";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { JobTypeLabel, ReqTermLabel } from "../requirement-my-sales/modal";
import { useEffect, useState } from "react";
import { useToast } from "../../context/toastProvider";
import { Fetch } from "../../utilities/fetch";
import { Button } from "primereact/button";
import { format } from "date-fns";
import { NoticePeriodLabel, SubmissionTypeLabel, TaxTermLabel } from "../requirement-my-recruiter/SubmissionForm";
import { ISubmittals } from "./SubmissionTable";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from 'primereact/inputtextarea';

interface Iprops {
  req?: TRequirement,
  submission?: ISubmittals,
  onSuccess: () => void,
  open: boolean,
  hideDialog: () => void,
}

export interface TSubmissionData {
  id: number;
  candidateId: number;
  name: string;
  email: string;
  homeNo: string;
  jobTitle: string;

  visaId: number;
  visaName: string;
  countryId: number;
  countryName: string;
  stateId: number;
  stateName: string;
  cityId: number;
  cityName: string;

  experienceInMonth: number;
  linkedin: string;
  dob: string;
  ssn: string;

  annualSalary: number;
  hourlyRate: number;
  noticePeriodInDays: number | null;
  buying_Rate: number;
  submittal_Rate: number;

  resumePath: string;
  status: boolean;

  candidateEducation: TCandidateEducation | null;

  addition_Info: string;
  faceToFaceInterview: boolean;

  tax_Term: number;
  submissionType: number;

  comment?: string;

  candidateEmployer: TCandidateEmployer | null;
}

export interface TCandidateEducation {
  id: number;
  candidateId: number;
  candidate: null;
  degree: string;
  passoutYear: number;
  university: string;
  percentage: number | null;
}

export interface TCandidateEmployer {
  vendorCompanyId: number;
  vendorCompanyName: string;
  contactPersonId: number;
  contactPersonName: string;
  contactPersonEmail: string;
  contactPersonPhoneNo: string;
  createdOn: string;
}

const InfoSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div
    style={{
      border: '1px solid #e2e8f0',
      borderRadius: '10px',
      overflow: 'hidden',
      background: '#fff',
    }}
  >
    <div
      style={{
        padding: '10px 14px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        fontSize: '13px',
        fontWeight: 600,
        color: '#334155',
      }}
    >
      {title}
    </div>

    <div style={{ padding: '14px' }}>
      {children}
    </div>
  </div>
);

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
    }}
  >
    <span
      style={{
        fontSize: '11px',
        color: '#94a3b8',
        fontWeight: 500,
      }}
    >
      {label}
    </span>

    <span
      style={{
        fontSize: '13px',
        color: '#334155',
        fontWeight: 500,
        wordBreak: 'break-word',
      }}
    >
      {value || '-'}
    </span>
  </div>
);

const RateCard = ({ label, value, }: { label: string; value: string; }) => (
  <div style={{
    padding: '12px', background: '#f8fafc',
    border: '1px solid #e2e8f0', borderRadius: '8px',
  }}>
    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', }}>
      {label}
    </div>
    <div style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', }}>
      {value || '-'}
    </div>
  </div>
);

export const Submission = (props: Iprops) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submissionData, setSubmissionData] = useState<TSubmissionData | null>(null);

  const [submittalRate, setSubmittalRate] = useState<number>(0);
  const [rejectComment, setRejectComment] = useState('');
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    if (submissionData) {
      setSubmittalRate(submissionData.submittal_Rate ?? 0);
      setRejectComment(submissionData.comment ?? "");
      setRejecting(false);
    }
  }, [submissionData]);

  useEffect(() => {
    if (props.submission) {
      fillSubmission(props.submission.submissionId);
    }
  }, [props.submission]);

  const fillSubmission = async (submissionId: number) => {
    setLoading(true);
    const data = await Fetch(`SalesMyRequirement/submission/${submissionId}`);
    if (data) {
      setSubmissionData(data);
    } else {
      setSubmissionData(null);
    }
    setLoading(false);
  };

  async function onAccept(submittalRate: number) {
    if (!props.submission) return;
    setLoading(true);
    const response = await Fetch(`SalesMyRequirement/accept/${props.submission.submissionId}`,
      {
        method: "POST",
        query: new URLSearchParams({ submittalRate: submittalRate.toString() }).toString(),
        onError: (message) => showToast({ severity: "error", detail: message })
      });
    if (response) {
      showToast({ severity: "success", detail: "Submission accepted successfully." });
      props.onSuccess();
      onHide();
    }
    setLoading(false);
  }

  async function onReject(rejectReason: string) {
    if (!props.submission) return;
    setLoading(true);
    const response = await Fetch(`SalesMyRequirement/reject/${props.submission.submissionId}`,
      {
        method: "POST",
        query: new URLSearchParams({ rejectionReason: rejectReason }).toString(),
        onError: (message) => showToast({ severity: "error", detail: message })
      });
    if (response) {
      showToast({ severity: "success", detail: "Submission rejected successfully." });
      props.onSuccess();
      onHide();
    }
    setLoading(false);
  }

  const onHide = () => {
    props.hideDialog();
  };

  const dFooter = () => {
    if (props.submission?.submissionStatusId !== 1) return null;
    return (rejecting ?
      <div className="col-12 md:col-12 flex gap-2 justify-content-end">
        <Button className="p-button-secondary p-button-outlined" onClick={() => setRejecting(false)}
          loading={loading} form="submissionForm" label="Cancel" type="button" />
        <Button className="p-button-danger" onClick={async () => {
          if (!rejectComment) {
            showToast({ severity: "error", detail: "Reject reason is required." });
            return;
          }
          await onReject(rejectComment);
        }} loading={loading} form="submissionForm" label="Confirm Reject" type="button" />
      </div> :
      <div className="col-12 md:col-12 flex gap-2 justify-content-end">
        <Button className="p-button-success" onClick={async () => {
          const buyingRate = submissionData?.buying_Rate;
          if (!buyingRate || submittalRate < buyingRate) {
            showToast({ severity: "error", detail: "Submittal Rate is required and should be greater than or equal to Buying Rate." });
            return;
          }
          await onAccept(submittalRate);
        }} loading={loading} form="submissionForm" label="Accept" type="button" />
        <Button className="p-button-danger p-button-outlined" onClick={() => setRejecting(true)}
          loading={loading} form="submissionForm" label="Reject" type="button" />
      </div>
    );
  };


  return (
    <Dialog visible={props.open} style={{ width: '80vw' }} header="Candidate Submission" footer={dFooter} modal className="p-fluid" onHide={onHide}>
      <DataTable size="small" value={props.req ? [props.req] : []} lazy dataKey="id" emptyMessage="No Requirement found.">
        <Column field="id" header="ReqId" />
        <Column field="jobTitle" header="Job Title" />
        <Column field="duration" header="Duration" body={(r: TRequirement) => `${r.duration} month(s)`} />
        <Column field="jobType" header="Job Type" body={(r: TRequirement) => JobTypeLabel[r.jobType]} />
        <Column field="term" header="Term" body={(r: TRequirement) => ReqTermLabel[r.term]} />
        <Column field="skillCategory" header="Skill Category" />
        <Column field="interviewProcess" header="Interview Process" />
        <Column field="vendorCompanyName" header="Client Company" />
        <Column field="contactPersonName" header="Contact Person" />
        <Column field="location" header="Location" body={(r: TRequirement) => r.cityId ? `${r.cityName}, ${r.stateName}, ${r.countryName}` : '--'} />
        <Column field="visa" header="Visa" body={(r: TRequirement) => <span>{r.visa?.map(v => v.name).join(', ')}</span>} />
        <Column field="postedBy" header="Posted By" />
        <Column field="postedOn" header="Posted On" body={(r: TRequirement) => format(new Date(r.postedOn + 'z'), "MM-dd-yyyy hh:mm a")} />
      </DataTable>

      {submissionData && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '4px',
            marginTop: '12px',
          }}
        >
          {/* Candidate Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '50%',
                  background: '#dbeafe',
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 700,
                }}
              >
                {submissionData.name?.charAt(0)?.toUpperCase()}
              </div>

              <div>
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                  }}
                >
                  {`${submissionData.name} (${props.submission?.submissionStatus})`}
                </div>

                <div
                  style={{
                    fontSize: '13px',
                    color: '#64748b',
                    marginTop: '2px',
                  }}
                >
                  {submissionData.jobTitle}
                </div>
              </div>
            </div>

            <span
              style={{
                padding: '5px 10px',
                borderRadius: '14px',
                background: submissionData.status
                  ? '#dcfce7'
                  : '#fee2e2',
                color: submissionData.status
                  ? '#15803d'
                  : '#dc2626',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {submissionData.status ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Contact & Location */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 2fr',
              gap: '12px',
            }}
          >
            <InfoSection title="Contact">
              <InfoRow label="Email" value={submissionData.email} />
              <InfoRow label="Phone" value={submissionData.homeNo} />
              <InfoRow
                label="LinkedIn"
                value={
                  submissionData.linkedin ? (
                    <a
                      href={submissionData.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2563eb',
                        textDecoration: 'none',
                      }}
                    >
                      LinkedIn Profile ↗
                    </a>
                  ) : (
                    '-'
                  )
                }
              />
            </InfoSection>

            <InfoSection title="Location">
              <InfoRow
                label="Country"
                value={submissionData.countryName}
              />
              <InfoRow
                label="State"
                value={submissionData.stateName}
              />
              <InfoRow label="City" value={submissionData.cityName} />
            </InfoSection>
            {/* Professional Information */}
            <InfoSection title="Professional Information">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                }}
              >
                <InfoRow
                  label="Job Title"
                  value={submissionData.jobTitle}
                />

                <InfoRow
                  label="Experience"
                  value={`${submissionData.experienceInMonth} year(s)`}
                />

                <InfoRow
                  label="Notice Period"
                  value={
                    submissionData.noticePeriodInDays ? `${NoticePeriodLabel[submissionData.noticePeriodInDays as keyof typeof NoticePeriodLabel] || submissionData.noticePeriodInDays}`
                      : 'Not specified'
                  }
                />

                <InfoRow
                  label="Visa Status"
                  value={submissionData.visaName || 'Not specified'}
                />

                <InfoRow
                  label="Tax Term"
                  value={submissionData.tax_Term ? TaxTermLabel[submissionData.tax_Term as keyof typeof TaxTermLabel] : 'Not specified'}
                />

                <InfoRow
                  label="Submission Type"
                  value={submissionData.submissionType ? SubmissionTypeLabel[submissionData.submissionType as keyof typeof SubmissionTypeLabel] : 'Not specified'}
                />
              </div>
            </InfoSection>
          </div>


          {/* Rates */}
          <InfoSection title="Rates">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
              }}
            >
              <RateCard
                label="Annual Salary"
                value={`${submissionData.annualSalary?.toLocaleString()}`}
              />

              <RateCard
                label="Hourly Rate"
                value={`${submissionData.hourlyRate}`}
              />

              <RateCard
                label="Buying Rate"
                value={`${submissionData.buying_Rate}`}
              />

              <div style={{
                padding: '12px', background: '#f8fafc',
                border: '1px solid #e2e8f0', borderRadius: '8px',
              }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', }}>Submittal Rate</div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', }}>
                  {props.submission?.submissionStatusId === 1 ?
                    <InputText type="number" value={submittalRate} onChange={(e) => setSubmittalRate(Number(e.target.value) || 0)} />
                    : submittalRate}
                </div>
              </div>

            </div>
          </InfoSection>

          {/* Education */}
          {submissionData.candidateEducation && (
            <InfoSection title="Education">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                }}
              >
                <InfoRow
                  label="Degree"
                  value={submissionData.candidateEducation.degree}
                />

                <InfoRow
                  label="University"
                  value={
                    submissionData.candidateEducation.university
                  }
                />

                <InfoRow
                  label="Passout Year"
                  value={
                    submissionData.candidateEducation.passoutYear
                  }
                />

                <InfoRow
                  label="Percentage"
                  value={
                    submissionData.candidateEducation.percentage ??
                    'Not specified'
                  }
                />
              </div>
            </InfoSection>
          )}

          {/* Employer */}
          {submissionData.candidateEmployer && (
            <InfoSection title="Current / Vendor Employer">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                }}
              >
                <InfoRow
                  label="Company"
                  value={
                    submissionData.candidateEmployer
                      .vendorCompanyName
                  }
                />

                <InfoRow
                  label="Contact Person"
                  value={
                    submissionData.candidateEmployer
                      .contactPersonName
                  }
                />

                <InfoRow
                  label="Email"
                  value={
                    submissionData.candidateEmployer
                      .contactPersonEmail
                  }
                />

                <InfoRow
                  label="Phone"
                  value={
                    submissionData.candidateEmployer
                      .contactPersonPhoneNo
                  }
                />
              </div>
            </InfoSection>
          )}

          {/* Additional Information */}
          {submissionData.addition_Info && (
            <InfoSection title="Additional Information">
              <div
                style={{
                  color: '#475569',
                  fontSize: '13px',
                  lineHeight: 1.6,
                }}
                dangerouslySetInnerHTML={{
                  __html: submissionData.addition_Info,
                }}
              />
            </InfoSection>
          )}

          {/* Reject comment */}
          {(rejecting || props.submission?.submissionStatusId === 3) && (
            <div style={{
              padding: '10px',
              background: '#fff7f7',
              border: '1px solid #fecaca',
              borderRadius: '6px',
            }}>
              <label style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#991b1b',
              }}>Rejection Reason</label>
              {props.submission?.submissionStatusId === 1 ?
                <InputTextarea
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  rows={3}
                  autoResize
                  placeholder="Enter reason for rejecting this candidate..."
                  style={{ width: '100%', fontSize: '12px', }}
                /> : <div>{rejectComment}</div>}
            </div>
          )}
        </div>
      )}

    </Dialog>
  );
};

export default Submission;
